import { useCallback, useEffect, useState, type FormEvent } from "react";
import { formatCad, type Product } from "@shared/modules/x-atelier";
import { ApiError } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { PageHeader } from "../../../core/components/ui/page-header";
import {
  createProduct,
  deleteProduct,
  listProducts,
  orderProducts,
  updateProduct,
} from "../lib/api";
import { centsToInput, parseAmountToCents } from "../lib/money";

const FORM_ID = "atelier-product-form";

interface Draft {
  name: string;
  supplier: string;
  unit: string;
  unitCost: string;
  active: boolean;
}

function draftFrom(p?: Product): Draft {
  return {
    name: p?.name ?? "",
    supplier: p?.supplier ?? "",
    unit: p?.unit ?? "unité",
    unitCost: centsToInput(p?.unitCostCents ?? 0),
    active: p?.active ?? true,
  };
}

/** Échange deux éléments voisins ; liste inchangée si le déplacement est impossible. */
function swap<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const target = index + dir;
  if (target < 0 || target >= list.length) return list;
  const out = [...list];
  [out[index], out[target]] = [out[target]!, out[index]!];
  return out;
}

/** Produits (admin) : ce que consomment les services, avec leur coût unitaire — base du coût de revient. */
export function ProduitsPage() {
  const push = useToastStore((s) => s.push);
  const [list, setList] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; product?: Product }>({ open: false });
  const [draft, setDraft] = useState<Draft>(() => draftFrom());
  const [confirm, setConfirm] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listProducts()
      .then((l) => {
        setList(l);
        setFailed(false);
      })
      .catch(() => {
        setList([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const open = (product?: Product) => {
    setDraft(draftFrom(product));
    setModal({ open: true, product });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    const unit = draft.unit.trim();
    if (!name || !unit || busy) return;
    const unitCostCents = parseAmountToCents(draft.unitCost);
    if (unitCostCents === null) {
      push("danger", "Coût unitaire illisible", "Saisissez un montant, par exemple 12,50.");
      return;
    }
    const input = {
      name,
      supplier: draft.supplier.trim() || null,
      unit,
      unitCostCents,
      active: draft.active,
    };
    setBusy(true);
    try {
      const p = modal.product;
      if (p) await updateProduct(p.id, input);
      else await createProduct(input);
      push("success", p ? "Produit mis à jour" : "Produit ajouté", name);
      setModal({ open: false });
      load();
    } catch {
      push("danger", "Enregistrement impossible", "Vérifiez les champs saisis.");
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, dir: -1 | 1) => {
    if (busy) return;
    const next = swap(list, index, dir);
    if (next === list) return;
    setList(next);
    setBusy(true);
    orderProducts(next.map((p) => p.id))
      .then(() => push("success", "Ordre des produits enregistré"))
      .catch(() => {
        push("danger", "Ordre non enregistré");
        load();
      })
      .finally(() => setBusy(false));
  };

  const remove = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    try {
      await deleteProduct(confirm.id);
      push("success", "Produit supprimé");
      setConfirm(null);
      load();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      push(
        "danger",
        "Suppression impossible",
        code === "product_in_use"
          ? "Ce produit est lié à un service : retirez-le des services ou désactivez-le."
          : undefined,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page produits">
      <PageHeader
        title="Produits"
        subtitle="Ce que consomment les services et son coût unitaire : la base du coût de revient et de la marge."
        actions={
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => open()}>
            Nouveau produit
          </Button>
        }
      />

      <table className="table">
        <thead>
          <tr>
            <th>Produit</th>
            <th>Fournisseur</th>
            <th>Unité</th>
            <th className="produits__num">Coût unitaire</th>
            <th>Actif</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((p, index) => (
            <tr key={p.id} className={p.active ? undefined : "produits__row--inactive"}>
              <td>
                <span className="produits__name">{p.name}</span>
              </td>
              <td>{p.supplier ?? <span className="produits__muted">—</span>}</td>
              <td>{p.unit}</td>
              <td className="produits__num">
                {p.unitCostCents !== undefined ? `${formatCad(p.unitCostCents)} / ${p.unit}` : "—"}
              </td>
              <td>
                {p.active ? (
                  <Badge variant="success">Actif</Badge>
                ) : (
                  <Badge variant="neutral">Inactif</Badge>
                )}
              </td>
              <td className="table__actions">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Monter ${p.name}`}
                  disabled={busy || index === 0}
                  icon={<Icon name="arrow-up" size={14} />}
                  onClick={() => move(index, -1)}
                >
                  Monter
                </Button>{" "}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Descendre ${p.name}`}
                  disabled={busy || index === list.length - 1}
                  icon={<Icon name="arrow-down" size={14} />}
                  onClick={() => move(index, 1)}
                >
                  Descendre
                </Button>{" "}
                <Button variant="ghost" size="sm" onClick={() => open(p)}>
                  Modifier
                </Button>{" "}
                <Button variant="ghost-danger" size="sm" onClick={() => setConfirm(p)}>
                  Supprimer
                </Button>
              </td>
            </tr>
          ))}
          {!loading && list.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                {failed
                  ? "Liste indisponible — réessayez dans un instant."
                  : "Aucun produit : ajoutez-en un pour chiffrer le coût de revient des services."}
              </td>
            </tr>
          ) : null}
          {loading ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Chargement…
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <Modal
        open={modal.open}
        title={modal.product ? "Modifier le produit" : "Nouveau produit"}
        onClose={() => setModal({ open: false })}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModal({ open: false })} disabled={busy}>
              Annuler
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="primary"
              disabled={busy || !draft.name.trim() || !draft.unit.trim()}
            >
              {modal.product ? "Enregistrer" : "Ajouter"}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className="atelier-form" onSubmit={(e) => void save(e)}>
          <div className="form-section__grid">
            <Field label="Nom">
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="ex : Pellicule PPF 60 cm"
                autoFocus
                required
              />
            </Field>
            <Field label="Fournisseur" hint="Facultatif.">
              <Input
                value={draft.supplier}
                onChange={(e) => setDraft({ ...draft, supplier: e.target.value })}
                placeholder="ex : XPEL"
              />
            </Field>
            <Field label="Unité" hint="Unité de la quantité consommée : ml, L, m, unité…">
              <Input
                value={draft.unit}
                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                placeholder="unité"
                required
              />
            </Field>
            <Field
              label="Coût unitaire ($)"
              hint="Par unité ci-contre. Visible des administrateurs seulement."
            >
              <Input
                inputMode="decimal"
                value={draft.unitCost}
                onChange={(e) => setDraft({ ...draft, unitCost: e.target.value })}
                placeholder="0,00"
              />
            </Field>
          </div>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            Actif (proposé dans les services)
          </label>
        </form>
      </Modal>

      <Modal
        open={confirm !== null}
        title="Supprimer ce produit ?"
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          « {confirm?.name ?? ""} » sera supprimé. Un produit encore lié à un service ne peut pas
          être supprimé : désactivez-le plutôt.
        </p>
      </Modal>
    </div>
  );
}
